import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getAuthHeader } from './leadsConstants';

const CALL_STATUS_OPTIONS = ['Connected', 'Not Connected'];
const FOLLOWUP_STATUS_OPTIONS = ['Spoke to Customer', 'Customer was Busy', 'No Response', 'Wrong Number', 'Callback Requested'];
const SUB_LEAD_STATUS_OPEN = ['EMI Related', 'Exchange Related', 'Test Drive Follow up', 'Visit Follow up', 'Home Test Drive', 'Price Enquiry', 'Finance Enquiry'];
const SUB_LEAD_STATUS_CLOSE = ['Delivery Done', 'Booking Done', 'Lost - Not Interested', 'Lost - Bought Elsewhere', 'Lost - Budget Issue'];

export function FollowUpModal({ lead, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = React.useState('add');
  const [followUpData, setFollowUpData] = React.useState({
    call_status: '',
    followup_status: '',
    sub_lead_status: '',
    sub_lead_category: 'open',
    next_follow_up_date: '',
    next_follow_up_time: '',
    remarks: ''
  });
  const [loading, setLoading] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [leadDetails, setLeadDetails] = React.useState(lead);

  React.useEffect(() => {
    fetchLeadDetails();
    fetchFollowUpHistory();
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeadDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}`, getAuthHeader());
      setLeadDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch lead details');
    }
  };

  const fetchFollowUpHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}/follow-up-history`, getAuthHeader());
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history');
      setHistory(leadDetails.activities || []);
    }
  };

  const handleSave = async () => {
    if (!followUpData.call_status) {
      toast.error('Please select Call Status');
      return;
    }
    
    setLoading(true);
    try {
      let nextFollowUp = followUpData.next_follow_up_date;
      if (nextFollowUp && followUpData.next_follow_up_time) {
        nextFollowUp = `${followUpData.next_follow_up_date}T${followUpData.next_follow_up_time}`;
      }

      await axios.post(`${BACKEND_URL}/api/leads/${lead.id}/follow-up`, {
        call_status: followUpData.call_status,
        followup_status: followUpData.followup_status,
        sub_lead_status: followUpData.sub_lead_status,
        next_follow_up_date: nextFollowUp,
        remarks: followUpData.remarks
      }, getAuthHeader());
      
      toast.success('Follow-up saved successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save follow-up');
    }
    setLoading(false);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Follow-up: {leadDetails.first_name} {leadDetails.last_name}
            </h3>
            <p className="text-sm text-gray-500">{leadDetails.mobile} &bull; {leadDetails.interested_model}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        {/* Content - 2 Column Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Column - Customer Details & Add Follow-up */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-4">
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('add')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'add' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Add Follow-up
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'details' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Customer Details
              </button>
            </div>

            {activeTab === 'add' ? (
              <div className="space-y-4">
                {/* Call Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Call Status *</label>
                  <div className="flex gap-2">
                    {CALL_STATUS_OPTIONS.map(status => (
                      <button
                        key={status}
                        onClick={() => setFollowUpData(prev => ({ ...prev, call_status: status }))}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                          followUpData.call_status === status
                            ? status === 'Connected' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Follow-up Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Status</label>
                  <select
                    value={followUpData.followup_status}
                    onChange={(e) => setFollowUpData(prev => ({ ...prev, followup_status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- Select --</option>
                    {FOLLOWUP_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Sub Lead Status */}
                {followUpData.followup_status === 'Spoke to Customer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sub Lead Status</label>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setFollowUpData(prev => ({ ...prev, sub_lead_category: 'open', sub_lead_status: '' }))}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          followUpData.sub_lead_category === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => setFollowUpData(prev => ({ ...prev, sub_lead_category: 'close', sub_lead_status: '' }))}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          followUpData.sub_lead_category === 'close' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Close
                      </button>
                    </div>
                    <select
                      value={followUpData.sub_lead_status}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, sub_lead_status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">-- Select --</option>
                      {(followUpData.sub_lead_category === 'open' ? SUB_LEAD_STATUS_OPEN : SUB_LEAD_STATUS_CLOSE).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Next Follow-up Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
                    <input
                      type="date"
                      value={followUpData.next_follow_up_date}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={followUpData.next_follow_up_time}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, next_follow_up_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={followUpData.remarks}
                    onChange={(e) => setFollowUpData(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Add notes about this follow-up..."
                  />
                </div>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-full py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                >
                  {loading ? 'Saving...' : 'Save Follow-up'}
                </button>
              </div>
            ) : (
              /* Customer Details Tab */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Name</span>
                    <p className="font-medium">{leadDetails.first_name} {leadDetails.last_name}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Mobile</span>
                    <p className="font-medium">{leadDetails.mobile}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Email</span>
                    <p className="font-medium">{leadDetails.email || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Lead Source</span>
                    <p className="font-medium">{leadDetails.lead_source}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Interest</span>
                    <p className="font-medium">{leadDetails.interested_brand} {leadDetails.interested_model}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Stage</span>
                    <p className="font-medium">{leadDetails.stage}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Priority</span>
                    <p className="font-medium">{leadDetails.priority}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Assigned To</span>
                    <p className="font-medium">{leadDetails.assigned_to || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Follow-up History */}
          <div className="w-1/2 overflow-y-auto p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-800 mb-3">Follow-up History</h4>
            {history.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No follow-up history yet</p>
            ) : (
              <div className="space-y-3">
                {history.map((item, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.call_status === 'Connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.call_status || item.activity_type || 'Activity'}
                      </span>
                      <span className="text-xs text-gray-500">{formatDateTime(item.timestamp || item.created_at)}</span>
                    </div>
                    {item.followup_status && (
                      <p className="text-sm text-gray-700"><strong>Status:</strong> {item.followup_status}</p>
                    )}
                    {item.sub_lead_status && (
                      <p className="text-sm text-gray-700"><strong>Sub Status:</strong> {item.sub_lead_status}</p>
                    )}
                    {item.remarks && (
                      <p className="text-sm text-gray-600 mt-1">{item.remarks}</p>
                    )}
                    {item.description && !item.remarks && (
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
